(function (global) {
    'use strict';

    global.NewHomeSource = global.NewHomeSource || {};
    global.NewHomeSource.BrochureHandler = global.NewHomeSource.BrochureHandler || {};

    const brochureHandler = {};

    /**
     * Generates a sanitized PDF file name for a brochure based on the provided listing name.
     *
     * If a valid listing name is provided, it is sanitized by trimming whitespace,
     * replacing spaces with underscores, and removing dots, then '.pdf' is appended.
     * If no valid listing name is given, a default file name is generated using the
     * current timestamp in the format 'brochure_YYYYMMDDHHMMSSmmm.pdf'.
     *
     * @param {string} listingName - The name of the listing to use in the file name.
     * @returns {string} The generated PDF file name.
     */
    const getBrochurePdfName = (listingName) => {
        // Helper: Pad single digits with leading zero
        const pad = (num, length = 2) => String(num).padStart(length, '0');

        // Generate timestamp in the format: YYYYMMDDHHMMSSmmm
        const now = new Date();
        const timestamp =
            now.getFullYear() +
            pad(now.getMonth() + 1) +
            pad(now.getDate()) +
            pad(now.getHours()) +
            pad(now.getMinutes()) +
            pad(now.getSeconds()) +
            pad(now.getMilliseconds(), 3); // 3-digit padding for milliseconds

        // Sanitize the listing name
        const sanitizedListingName = (listingName && listingName !== "")
            ? listingName
                .replace(/^\s+|\s+$/g, '')     // Trim leading/trailing whitespace
                .replace(/\s+/g, '_')          // Replace spaces with underscores
                .replace(/\./g, '')            // Remove dots
            : null;

        // Determine final file name
        const fileName = sanitizedListingName
            ? sanitizedListingName + '.pdf'
            : 'brochure_' + timestamp + '.pdf';

        return fileName;
    }

    /**
     * Downloads a PDF brochure from the given URL and saves it with a dynamic filename.
     * If a valid listing name is provided, it will be included in the filename; otherwise,
     * a default name with a timestamp is used.
     *
     * @param {string} url - The URL of the PDF to download.
     * @param {string} listingName - (Optional) The name of the listing to include in the file name.
     */
    const downloadBrochurePdfFromUrl = async (url, listingName) => {

        const response = await fetch(url, { mode: 'cors' });
        if (!response.ok) return;

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        // To trigger the download, we dynamically create an <a> (anchor) element.
        // Browsers only allow programmatic file downloads when initiated by user interaction,
        // so simulating a click on an <a> with a download attribute is a workaround.
        const link = document.createElement('a');

        // Set the href to the object URL containing the PDF blob
        link.href = blobUrl;

        // Set the desired file name for the downloaded file
        link.download = getBrochurePdfName(listingName);

        // Append the link to the DOM (required in some browsers like Firefox)
        document.body.appendChild(link);

        // Programmatically trigger a click to start the download
        link.click();

        // Remove the temporary link element from the DOM to keep things clean
        document.body.removeChild(link);

        // Release the memory used by the object URL now that it's no longer needed
        window.URL.revokeObjectURL(blobUrl);
    };

    /**
     * This function downloads a PDF file from a Blob object by creating a temporary anchor (<a>) element,
     * setting its `href` to a Blob URL, and triggering a click event. After the download starts, it cleans up
     * by removing the anchor element and revoking the Blob URL to free memory.
     *
     * @param {Blob} blob - The Blob object representing the PDF file.
     * @param {string} fileName - The desired name for the downloaded file.
     */
    const downloadBrochurePdfFromBlob = (blob, fileName) => {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = getBrochurePdfName(fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Release memory
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    };

    /**
     * @private
     * isValidUrl: Validates if a given string is a valid URL.
     * @param {string} url - The URL to validate.
     * @returns {boolean} - Returns true if the URL is valid.
     */
    const isValidUrl = (url) => {
        return url && typeof url === "string" && url.trim().length > 0;
    };

    /**
     * @public
     * getPdfBrochureUrl: Fetches the PDF brochure URL based on the current page.
     * @param {Object} listingInfo - The listing details.
     * @returns {Promise<string|null>} - Returns a promise that resolves to the URL or null.
     */
    const getPdfBrochureUrl = (listingInfo) => {
        const { pdfBrochureType, id } = getPdfBrochureApiUrlParams(listingInfo);

        if (!id || !pdfBrochureType) {
            return Promise.resolve(null);
        }

        const apiUrl = `ajax/brochures/getpdfbrochureurl?id=${id}&brochuretype=${pdfBrochureType}`;

        return fetch(apiUrl)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.json();
            })
            .then((response) => {
                if (response.Success && response.encryptedPdfBrochureUrl) {
                    return response.encryptedPdfBrochureUrl;
                } else {
                    return null;
                }
            })
            .catch(() => null);
    };

    /**
     * @private
     * getPdfBrochureApiUrlParams: Determines the parameters needed for the PDF brochure API URL.
     * @param {Object|null} listingInfo - The listing details. Can be null or undefined.
     * @returns {{ pdfBrochureType: string | null, id: number | null }} - Returns an object with the brochure type and ID or { null, null } if no valid data.
     */
    const getPdfBrochureApiUrlParams = (listingInfo) => {
        if (!listingInfo) {
            return { pdfBrochureType: null, id: null };
        }

        let pdfBrochureType = null;
        let id = null;

        // Convert string values to boolean or numbers as needed
        const isMPCommunity = listingInfo.isMPCommunity && listingInfo.isMPCommunity.toLowerCase() === "true";
        const planId = parseInt(listingInfo.planId) || 0;
        const specId = parseInt(listingInfo.specId) || 0;
        const communityId = parseInt(listingInfo.communityId) || 0;

        // Check for "plan"
        if (planId > 0 && specId === 0) {
            pdfBrochureType = "houseplan";
            id = planId;
        }
        // Check for "spec"
        else if (specId > 0) {
            pdfBrochureType = "housingunit";
            id = specId;
        }
        // Check for "MPC"
        else if (isMPCommunity) {
            pdfBrochureType = "masterplannedcommunity";
            id = communityId;
        }
        // Otherwise, "community"
        else if (communityId > 0) {
            pdfBrochureType = "community";
            id = communityId;
        }

        return { pdfBrochureType, id };
    };

    /**
     * This asynchronous function fetches a PDF file from a given URL and returns it as a Blob object.
     * It uses the Fetch API with CORS enabled. If the fetch fails (non-OK response), it throws an error.
     *
     * @param {string} url - The URL from which to fetch the PDF file.
     * @returns {Promise<Blob>} - A promise that resolves to the PDF Blob.
     * @throws {Error} - If the fetch operation fails.
     */
    const getPdfBrochureBlob = async (url) => {
        const response = await fetch(url, { mode: 'cors' });
        return await response.blob();
    };

    /**
     * @private
     * getPdfBrochure: Displays the PDF brochure when the URL is already provided.
     * @param {string} pdfBrochureUrl - The URL of the brochure.
     */
    const getPdfBrochure = (pdfBrochureUrl, listingName = "", blob = "") => {
        if (isValidUrl(pdfBrochureUrl)) {
            if (blob === "" || blob === null) {
                downloadBrochurePdfFromUrl(pdfBrochureUrl, listingName);
            }
            else {
                downloadBrochurePdfFromBlob(blob, listingName);
            }
        }
    };

    brochureHandler.getPdfBrochure = getPdfBrochure;
    brochureHandler.getPdfBrochureUrl = getPdfBrochureUrl;
    brochureHandler.getPdfBrochureBlob = getPdfBrochureBlob;

    // Assign the brochureHandler to the global object
    global.NewHomeSource.BrochureHandler = brochureHandler;

})(window);